import { useState, useEffect, useRef } from 'react';
import { etsyAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Loader2, Upload, Link as LinkIcon, ClipboardPaste, Sparkles, FileImage, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { TennisPriceInputs } from './TennisPriceInputs';
import { isTennis as isTennisType, IGI_UPCHARGE, TENNIS_CONFIG, tennisPricesComplete, tennisPricesPayload } from '../../lib/tennis';

const DESCRIPTION_TYPES = [
  { value: 'standard', label: 'Standard', desc: '300–500 words. Balanced trust + SEO.' },
  { value: 'premium',  label: 'Premium',  desc: '350–600 words. Private-jeweler tone for higher-ticket pieces.' },
  { value: 'mobile',   label: 'Mobile',   desc: '180–300 words. Short, scannable, mobile-first.' },
];

export function AddProductDialog({ open, onClose, onCreated }) {
  const [mode, setMode] = useState('url');     // 'url' | 'image' | 'text'
  const [descType, setDescType] = useState('standard');
  const [productType, setProductType] = useState('engagement_ring');
  const [isSpecialtyCut, setIsSpecialtyCut] = useState(false);
  const [bandCarat, setBandCarat] = useState('0');
  const [tennisPrices, setTennisPrices] = useState({});
  const [name, setName] = useState('');
  const [visualRef, setVisualRef] = useState('');
  const [url, setUrl] = useState('');
  const [text, setText] = useState('');
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [creating, setCreating] = useState(false);
  const [progress, setProgress] = useState('');
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) {
      // Reset on close
      setMode('url'); setDescType('standard'); setName('');
      setProductType('engagement_ring'); setIsSpecialtyCut(false); setBandCarat('0'); setTennisPrices({});
      setUrl(''); setText(''); setFile(null); setVisualRef('');
      if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }
      setCreating(false); setProgress('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const pickFile = (f) => {
    if (!f) return;
    if (!f.type.startsWith('image/')) { toast.error('Please upload an image (PNG / JPG / WEBP)'); return; }
    if (f.size > 20 * 1024 * 1024) { toast.error('File too large (max 20 MB)'); return; }
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
  };

  const submit = async () => {
    if (mode === 'url' && !url.trim()) { toast.error('Paste a URL'); return; }
    if (mode === 'image' && !file) { toast.error('Upload a CAD or product image'); return; }
    if (mode === 'text' && text.trim().length < 20) { toast.error('Paste at least the product title and a few specs'); return; }
    const isTennis = isTennisType(productType);
    if (isTennis && !tennisPricesComplete(tennisPrices, productType)) { toast.error(`Enter the Etsy price for every carat (${TENNIS_CONFIG[productType].carats.join(', ')} CT)`); return; }
    try {
      setCreating(true);
      setProgress('Generating title and description in parallel...');
      const fd = new FormData();
      fd.append('input_type', mode);
      fd.append('description_type', descType);
      fd.append('product_type', productType);
      fd.append('is_specialty_cut', isSpecialtyCut && !isTennis ? 'true' : 'false');
      if (isTennis) fd.append('tennis_prices', JSON.stringify(tennisPricesPayload(tennisPrices, productType)));
      const bandCaratNum = parseFloat(bandCarat || '0');
      fd.append('band_setting_stone_carat_weight', String(Number.isFinite(bandCaratNum) ? bandCaratNum : 0));
      if (name.trim()) fd.append('name', name.trim());
      if (visualRef.trim()) fd.append('visual_reference_link', visualRef.trim());
      if (mode === 'url') fd.append('input_url', url.trim());
      if (mode === 'text') fd.append('input_text', text.trim());
      if (mode === 'image' && file) fd.append('file', file);
      const res = await etsyAPI.create(fd);
      toast.success('Generating your listing pack…');
      onCreated(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create product');
    } finally {
      setCreating(false);
      setProgress('');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !creating) onClose(); }}>
      <DialogContent className="sm:max-w-2xl bg-white text-zinc-900 max-h-[90vh] overflow-y-auto" data-testid="add-etsy-product-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">Add Etsy Product</DialogTitle>
        </DialogHeader>
        <div className="space-y-5 mt-2">
          {/* Description type */}
          <div>
            <Label className="text-xs uppercase tracking-wider font-semibold text-zinc-700">Description type</Label>
            <div className="grid grid-cols-3 gap-2 mt-2">
              {DESCRIPTION_TYPES.map(t => (
                <button
                  key={t.value}
                  onClick={() => setDescType(t.value)}
                  className={`text-left rounded-md border p-3 transition-colors ${
                    descType === t.value
                      ? 'bg-rose-50 border-rose-300 ring-2 ring-rose-200'
                      : 'bg-white border-zinc-200 hover:border-zinc-300'
                  }`}
                  data-testid={`desc-type-${t.value}`}
                >
                  <div className="text-sm font-semibold text-zinc-900 capitalize">{t.label}</div>
                  <div className="text-[11px] text-zinc-500 mt-1 leading-snug">{t.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Product type — drives the pricing formula */}
          <div>
            <Label className="text-xs uppercase tracking-wider font-semibold text-zinc-700">Product type</Label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
              {[
                { value: 'engagement_ring',     label: 'Engagement Ring',     desc: 'Standard center-stone ring' },
                { value: 'engagement_ring_set', label: 'Engagement Ring Set', desc: 'Ring + matching wedding band' },
                { value: 'wedding_band',        label: 'Wedding Band Only',   desc: 'Standalone band, no center stone' },
                { value: 'pendant_studs',       label: 'Pendant / Studs',     desc: 'Pendant or stud earrings' },
                { value: 'tennis_bracelet',     label: 'Tennis Bracelet',     desc: '3–15 CT · 14K white or yellow gold' },
                { value: 'tennis_necklace',     label: 'Tennis Necklace',     desc: '5–25 CT · 14K white or yellow gold' },
              ].map((t) => (
                <button
                  key={t.value}
                  onClick={() => setProductType(t.value)}
                  className={`text-left rounded-md border p-3 transition-colors ${
                    productType === t.value
                      ? 'bg-rose-50 border-rose-300 ring-2 ring-rose-200'
                      : 'bg-white border-zinc-200 hover:border-zinc-300'
                  }`}
                  data-testid={`product-type-${t.value}`}
                >
                  <div className="text-sm font-semibold text-zinc-900">{t.label}</div>
                  <div className="text-[11px] text-zinc-500 mt-1 leading-snug">{t.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {isTennisType(productType) && (
            <div className="bg-rose-50/50 border border-rose-200 rounded-md p-3 space-y-2.5" data-testid="tennis-price-prompt">
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold text-zinc-700">Etsy price per carat · required</Label>
                <p className="text-[11px] text-zinc-500 mt-0.5 leading-snug">
                  Type the final Etsy price for the non-certified {TENNIS_CONFIG[productType].noun}. No sale markup is added. IGI certified = +${IGI_UPCHARGE}. 14K white and yellow gold cost the same. {TENNIS_CONFIG[productType].priceNote}
                </p>
              </div>
              <TennisPriceInputs productType={productType} value={tennisPrices} onChange={setTennisPrices} testIdPrefix="add-tennis-price" inputClassName="bg-white border-zinc-200 text-zinc-900" />
            </div>
          )}

          {/* Specialty cut + band setting stone carat (band carat only for set/band) */}
          {!isTennisType(productType) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="bg-zinc-50 border border-zinc-200 rounded-md p-3">
              <Label className="text-xs uppercase tracking-wider font-semibold text-zinc-700">Specialty cut?</Label>
              <p className="text-[11px] text-zinc-500 mt-0.5 leading-snug">Specialty cuts (e.g. radiant, princess) price the center stone at $250/ct instead of $150/ct.</p>
              <div className="flex items-center gap-2 mt-2">
                {[
                  { value: false, label: 'No · $150/ct' },
                  { value: true,  label: 'Yes · $250/ct' },
                ].map((o) => (
                  <button
                    key={String(o.value)}
                    onClick={() => setIsSpecialtyCut(o.value)}
                    className={`flex-1 text-[12px] py-2 px-3 rounded-md border transition-colors ${
                      isSpecialtyCut === o.value
                        ? 'bg-rose-50 border-rose-300 text-rose-900 font-medium'
                        : 'bg-white border-zinc-200 text-zinc-700 hover:border-zinc-300'
                    }`}
                    data-testid={`specialty-cut-${o.value ? 'yes' : 'no'}`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>

            {productType === 'wedding_band' ? (
              <div className="bg-zinc-50 border border-zinc-200 rounded-md p-3">
                <Label className="text-xs uppercase tracking-wider font-semibold text-zinc-700">Band setting stone carat</Label>
                <p className="text-[11px] text-zinc-500 mt-0.5 leading-snug">Total carat weight of the band's setting stones. Priced at $200/ct.</p>
                <Input
                  type="number"
                  step="0.05"
                  min="0"
                  max="10"
                  value={bandCarat}
                  onChange={(e) => setBandCarat(e.target.value)}
                  placeholder="0.00"
                  className="mt-2 bg-white border-zinc-200 text-zinc-900 h-9"
                  data-testid="band-stone-carat-input"
                />
                <div className="text-[11px] text-zinc-500 mt-1">You can edit this later in the pricing panel.</div>
              </div>
            ) : productType === 'engagement_ring_set' ? (
              <div className="bg-zinc-50 border border-zinc-200 rounded-md p-3 text-[11px] text-zinc-600">
                <div className="font-semibold text-zinc-700 mb-1">Band setting stones</div>
                We'll auto-extract the band's setting-stone carat weight from the listing description and you can edit it inline on the pricing panel — same as the engagement ring's side stones.
              </div>
            ) : productType === 'pendant_studs' ? (
              <div className="bg-zinc-50 border border-zinc-200 rounded-md p-3 text-[11px] text-zinc-600">
                <div className="font-semibold text-zinc-700 mb-1">Side stones (optional)</div>
                We'll auto-extract the side-stone carat weight from the listing description (priced at $120/ct). Pendants use a lighter metal table — Silver $100, 10K $175, 14K $225, 18K $300, Platinum $275.
              </div>
            ) : (
              <div className="bg-zinc-50 border border-dashed border-zinc-200 rounded-md p-3 text-[11px] text-zinc-400 flex items-center">
                Band stones only apply to ring sets or standalone bands.
              </div>
            )}
          </div>
          )}

          {/* Mode */}
          <div>
            <Label className="text-xs uppercase tracking-wider font-semibold text-zinc-700">Source</Label>
            <div className="flex items-center gap-1 bg-zinc-100 p-1 rounded-lg mt-2">
              {[
                { value: 'url',   label: 'URL',          icon: LinkIcon },
                { value: 'image', label: 'Upload image', icon: Upload },
                { value: 'text',  label: 'Paste text',   icon: ClipboardPaste },
              ].map(t => {
                const Icon = t.icon;
                return (
                  <button
                    key={t.value}
                    onClick={() => setMode(t.value)}
                    className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                      mode === t.value ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-800'
                    }`}
                    data-testid={`add-etsy-mode-${t.value}`}
                  >
                    <Icon size={12} /> {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          {mode === 'url' && (
            <div>
              <Label className="text-zinc-700">Product URL</Label>
              <Input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://www.etsy.com/listing/... or any product page"
                className="mt-1 bg-white border-zinc-200 text-zinc-900"
                data-testid="add-etsy-url-input"
              />
              <p className="text-[11px] text-zinc-500 mt-1.5">Etsy / Amazon / Shopify pages auto-route through our proxy if blocked.</p>
            </div>
          )}

          {mode === 'image' && (
            <div>
              <Label className="text-zinc-700">CAD or product image</Label>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => pickFile(e.target.files?.[0])}
                data-testid="add-etsy-file-input"
              />
              {previewUrl ? (
                <div className="mt-2 relative aspect-video bg-zinc-50 rounded-md border border-zinc-200 overflow-hidden">
                  <img src={previewUrl} alt="Preview" className="w-full h-full object-contain" />
                  <button
                    onClick={() => { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); setFile(null); }}
                    className="absolute top-2 right-2 bg-white/90 backdrop-blur p-1.5 rounded-full hover:bg-white shadow-sm"
                    title="Remove"
                  >
                    <X size={12} />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => fileRef.current?.click()}
                  className="mt-2 w-full aspect-video border-2 border-dashed border-zinc-200 rounded-md hover:border-zinc-300 hover:bg-zinc-50 transition-colors flex flex-col items-center justify-center gap-2"
                  data-testid="add-etsy-upload-zone"
                >
                  <FileImage size={24} className="text-zinc-400" />
                  <span className="text-sm text-zinc-600 font-medium">Click to upload</span>
                  <span className="text-[11px] text-zinc-400">PNG / JPG / WEBP up to 20 MB</span>
                </button>
              )}
            </div>
          )}

          {mode === 'text' && (
            <div>
              <Label className="text-zinc-700">Paste product details</Label>
              <Textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={9}
                placeholder={"Paste the product title, description, and specs here.\n\nExample:\n2.5ct Round Lab Grown Diamond Engagement Ring, IGI Certified, 14K White Gold, Hidden Halo, D color VS1 clarity, prong setting..."}
                className="mt-1 bg-white border-zinc-200 text-zinc-900 resize-none font-mono text-sm leading-relaxed"
                data-testid="add-etsy-text-input"
              />
            </div>
          )}

          <div>
            <Label className="text-zinc-700">Product name (optional)</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="If blank, we'll auto-name from the best title"
              className="mt-1 bg-white border-zinc-200 text-zinc-900"
              data-testid="add-etsy-name-input"
            />
          </div>

          <div>
            <Label className="text-zinc-700 inline-flex items-center gap-1.5">
              <LinkIcon size={13} /> Add Visual Reference Link <span className="text-zinc-400 font-normal">(optional)</span>
            </Label>
            <Input
              value={visualRef}
              onChange={(e) => setVisualRef(e.target.value)}
              placeholder="https://… link for the visuals / ads team"
              className="mt-1 bg-white border-zinc-200 text-zinc-900"
              data-testid="add-etsy-visual-ref-input"
            />
            <p className="text-[11px] text-zinc-500 mt-1.5">Shown on the listing so the visuals team can reference it when creating ads. You can also add it later.</p>
          </div>

          {creating && (
            <div className="bg-rose-50 border border-rose-100 rounded-md px-4 py-3 flex items-center gap-2 text-xs text-rose-800">
              <Loader2 size={14} className="animate-spin" />
              {progress || 'Generating...'}
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <Button variant="outline" disabled={creating} className="flex-1 border-zinc-200 text-zinc-700" onClick={onClose}>Cancel</Button>
            <Button
              onClick={submit}
              disabled={creating}
              className="flex-1 bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="add-etsy-submit-btn"
            >
              {creating
                ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Generating…</>
                : <><Sparkles size={14} className="mr-1.5" /> Generate &amp; save</>}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
